import React, { useCallback, useEffect, useState } from 'react';
import { Eye, Link2, Pencil, Plus, Users, X } from 'lucide-react';
import { GovernanceBreadcrumbs, RiskBadge, btnPrimary, btnSecondary, inputClass, selectClass } from '../components/shared';
import { aiGovernanceService, GroupItem } from '../../../../components/api/aiGovernanceService';

export default function PolicyGroupsScreen() {
  const [groups, setGroups] = useState<GroupItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [editGroup, setEditGroup] = useState<GroupItem | null>(null);
  const [form, setForm] = useState({ name: '', description: '', ownerName: '', riskLevel: 'Medium' });
  const [saving, setSaving] = useState(false);

  const fetchGroups = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await aiGovernanceService.getGroups({ page: 1, pageSize: 100 });
      setGroups(res.items || []);
    } catch {
      setError('Failed to load policy groups.');
      setGroups([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchGroups();
  }, [fetchGroups]);

  const openCreate = () => {
    setEditGroup(null);
    setForm({ name: '', description: '', ownerName: '', riskLevel: 'Medium' });
    setShowCreate(true);
  };

  const openEdit = (g: GroupItem) => {
    setEditGroup(g);
    setForm({ name: g.name, description: g.description, ownerName: g.owner, riskLevel: g.riskLevel });
    setShowCreate(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      setError('Group name is required.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      if (editGroup) {
        await aiGovernanceService.updateGroup(editGroup.id, {
          name: form.name,
          description: form.description,
          ownerName: form.ownerName,
          riskLevel: form.riskLevel,
        });
      } else {
        await aiGovernanceService.createGroup({
          name: form.name,
          description: form.description,
          ownerName: form.ownerName,
          riskLevel: form.riskLevel,
        });
      }
      setShowCreate(false);
      await fetchGroups();
    } catch {
      setError('Failed to save group. Name must be unique.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4">
      <GovernanceBreadcrumbs items={[{ label: 'AI Governance' }, { label: 'Policy Groups' }]} />

      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-lg font-semibold text-slate-900">Policy Groups</h1>
          <p className="text-xs text-slate-500 mt-0.5">Organize policies by department, function, or risk profile for streamlined assignment.</p>
        </div>
        <button onClick={openCreate} className={btnSecondary}><Users className="w-3.5 h-3.5" /> Create Group</button>
      </div>

      {error && <div className="bg-red-50 border border-red-200 text-red-700 text-xs rounded-lg px-3 py-2">{error}</div>}

      {loading ? (
        <p className="text-sm text-slate-500">Loading groups...</p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
          {groups.length === 0 ? (
            <p className="text-sm text-slate-500 col-span-full">No groups yet. Create one to get started.</p>
          ) : groups.map((group) => (
            <div key={group.id} className="bg-white border border-slate-200 rounded-lg shadow-sm hover:shadow-md transition-shadow">
              <div className="px-4 py-3 border-b border-slate-100">
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="text-sm font-semibold text-slate-800">{group.name}</h3>
                    <p className="text-[10px] text-slate-500 mt-0.5 line-clamp-2">{group.description}</p>
                  </div>
                  <RiskBadge level={group.riskLevel} />
                </div>
              </div>
              <div className="px-4 py-3 grid grid-cols-2 gap-2 text-xs">
                <div>
                  <p className="text-[10px] text-slate-400">Policies</p>
                  <p className="font-semibold text-slate-800">{group.policyCount}</p>
                </div>
                <div>
                  <p className="text-[10px] text-slate-400">Assigned Assets</p>
                  <p className="font-semibold text-slate-800">{group.assignedAssets}</p>
                </div>
                <div>
                  <p className="text-[10px] text-slate-400">Owner</p>
                  <p className="text-slate-700">{group.owner || '—'}</p>
                </div>
                <div>
                  <p className="text-[10px] text-slate-400">Last Updated</p>
                  <p className="text-slate-500">{group.lastUpdated}</p>
                </div>
              </div>
              <div className="px-4 py-2 border-t border-slate-100 flex items-center gap-2">
                <button onClick={() => openEdit(group)} className="flex items-center gap-1 text-[10px] font-medium text-indigo-600 hover:text-indigo-800"><Eye className="w-3 h-3" /> View</button>
                <button onClick={() => openEdit(group)} className="flex items-center gap-1 text-[10px] font-medium text-slate-600 hover:text-slate-800"><Pencil className="w-3 h-3" /> Edit</button>
                <button className="flex items-center gap-1 text-[10px] font-medium text-slate-600 hover:text-slate-800"><Link2 className="w-3 h-3" /> Assign</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {showCreate && (
        <div className="fixed inset-0 z-[200] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl border border-slate-200 w-full max-w-md p-5">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-slate-900">{editGroup ? 'Edit Group' : 'Create Group'}</h3>
              <button onClick={() => setShowCreate(false)} className="p-1 rounded hover:bg-slate-100 text-slate-400"><X className="w-4 h-4" /></button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="block text-[11px] font-medium text-slate-600 mb-1">Name</label>
                <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputClass} />
              </div>
              <div>
                <label className="block text-[11px] font-medium text-slate-600 mb-1">Description</label>
                <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} className={inputClass} />
              </div>
              <div>
                <label className="block text-[11px] font-medium text-slate-600 mb-1">Owner</label>
                <input value={form.ownerName} onChange={(e) => setForm({ ...form, ownerName: e.target.value })} className={inputClass} />
              </div>
              <div>
                <label className="block text-[11px] font-medium text-slate-600 mb-1">Risk Level</label>
                <select value={form.riskLevel} onChange={(e) => setForm({ ...form, riskLevel: e.target.value })} className={selectClass}>
                  <option>Low</option><option>Medium</option><option>High</option><option>Critical</option>
                </select>
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={() => setShowCreate(false)} className={btnSecondary}>Cancel</button>
              <button onClick={handleSave} disabled={saving} className={btnPrimary}><Plus className="w-3.5 h-3.5" /> {saving ? 'Saving...' : 'Save'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
